import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import useStockVarianceReportRepositories from "repositories/stock-variance-report"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")
const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || toInputDate(new Date(Date.now() - 86400000))
  const dateTo = searchParams.get("date_to") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const search = searchParams.get("search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"
  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => setSearchInput(search), [search])
  const submitSearch = () => {
    if (searchInput === search) return
    const nextParams = new URLSearchParams(searchParams)
    if (searchInput.trim() === "") nextParams.delete("search")
    else nextParams.set("search", searchInput.trim())
    nextParams.set("page", "1")
    setSearchParams(nextParams)
  }

  const { data: reportData, isLoading } = useStockVarianceReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    search,
    page,
    per_page: perPage,
    enabled: true,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.rows ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items

  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum ekspor")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    })

    if (resolvedOutletCsv) query.set("outlet_id", resolvedOutletCsv)
    if (search) query.set("search", search)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/stock-variance/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Selisih Fisik / Computer</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Selisih antara stock computer dan stock fisik berdasarkan opname terakhir per produk di periode.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end xl:justify-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Outlet</label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[180px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Cari</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitSearch() } }}
                placeholder="Kode / nama barang"
              />
              <button
                type="button"
                onClick={submitSearch}
                className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
              >
                Cari
              </button>
            </div>
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Awal</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Tanggal Akhir</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">Per Page</label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[25, 50, 100, 200, 500].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Baris</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_rows)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Qty Computer</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_computer_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Qty Fisik</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_physical_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Nilai Lebih</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-700">{formatCurrency(summary.total_excess_value)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Nilai Kurang</div>
          <div className="mt-2 text-2xl font-semibold text-rose-700">{formatCurrency(summary.total_short_value)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Net</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency((Number(summary.total_excess_value ?? 0) - Number(summary.total_short_value ?? 0)))}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">LAPORAN SELISIH FISIK / COMPUTER &middot; {reportData?.period_label || "-"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">KODE BARANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">HARGA</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">COMPUTER</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">NILAI COMPUTER</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">FISIK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">NILAI FISIK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">LEBIH</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">NILAI+</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">KURANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">NILAI-</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-neutral-500">Memuat data laporan...</td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-neutral-500">Tidak ada selisih stock untuk periode yang dipilih.</td>
                </tr>
              )}

              {!isLoading && rows.map((row, index) => (
                <tr key={row.product_unit_id || `${row.product_code}-${index}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap text-neutral-900">{row.product_code || "-"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-800">{formatCurrency(row.price)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-800">{formatNumber(row.computer_qty)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-800">{formatCurrency(row.computer_value)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-800">{formatNumber(row.physical_qty)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-neutral-800">{formatCurrency(row.physical_value)}</td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${Number(row.excess_qty ?? 0) > 0 ? "text-emerald-700 font-semibold" : "text-neutral-500"}`}>{formatNumber(row.excess_qty)}</td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${Number(row.excess_value ?? 0) > 0 ? "text-emerald-700 font-semibold" : "text-neutral-500"}`}>{formatCurrency(row.excess_value)}</td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${Number(row.short_qty ?? 0) > 0 ? "text-rose-700 font-semibold" : "text-neutral-500"}`}>{formatNumber(row.short_qty)}</td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${Number(row.short_value ?? 0) > 0 ? "text-rose-700 font-semibold" : "text-neutral-500"}`}>{formatCurrency(row.short_value)}</td>
                </tr>
              ))}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900" colSpan={2}>TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_computer_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_computer_value)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_physical_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_physical_value)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-emerald-700">{formatNumber(summary.total_excess_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-emerald-700">{formatCurrency(summary.total_excess_value)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-rose-700">{formatNumber(summary.total_short_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-rose-700">{formatCurrency(summary.total_short_value)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}
